import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarComponent } from "./Sidebar";
import { NavbarComponent } from "./Navbar";
import { Button, Modal } from "flowbite-react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import useApi from "../hooks/useApi";
import configureService from "../api/services/configureService";
import ConfigHeader from "../views/PostLogin/Configure/ConfigHeader";
import { useDispatch, useSelector } from "react-redux";
import { clearUser } from "../redux/slices/userSlice";
import activityLogService from "../api/services/activityLogService";
import Loader from "./Loader";
import loginService from "../api/services/loginService";

const Dashboard = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  // const loginData = JSON.parse(localStorage.getItem("loginData" || "{}"));
  const loginData = useSelector((state) => state.user.loginData);
  const [advertiserId, setAdvertiserId] = useState(null);
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [advertisersList, setAdvertisersList] = useState([]);
  const [selectedAdvertiser, setSelectedAdvertiser] = useState("");
  const [meta_followers, setMetaFollowers] = useState("");
  const [insta_followers, setInsFollowers] = useState("");
  const [advLogo, setAdvLogo] = useState(null);
  const [dataIngestionStatus, setDataIngestionStatus] = useState(null);
  const [googleIngestionStatus, setGoogleIngestionStatus] = useState(null);
  const [optIngestionStatus, setOptIngestionStatus] = useState(null);
  // const [search, setSearch] = useState("");
  const [noAdvertiser, setNoAdvertiser] = useState(false);
  const [isAdvLoading, setIsAdvLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  // const [startDate, setStartDate] = useState("");
  // const [endDate, setEndDate] = useState("");
  const { callApi: getAdvertisers } = useApi(
    configureService.getAdvertisers,
    null,
    false,
  );
  const { callApi: addActivityLog } = useApi(
    activityLogService.addActivityLog,
    null,
    false,
  );
  const [tabs, setTabs] = useState([]);
  const { callApi: userLogout } = useApi(loginService.userLogout, null, false);
  const { callApi: advertiserTabAccess } = useApi(
    configureService.advertiserTabAccess,
    false,
  );
  const tabRoutes = {
    // Parent tabs
    Audit: "/audit-report",
    Planning: "/media-planning",
    "Optimisation AI": "/optimization-status",
    // "Campaign Analytics": "/campaign-analytics",
    // "Creative AI": "/creative-ai",
    // "Audience AI": "/audience",
    "Feed Manager": "/feed-setting",

    // Sub tabs (nested pages)
    Meta: "/audit-report",
    Google: "/google-audit",

    Status: "/optimization-status",
    "Take Off": "/optimization-take-off",
    "Co-Pilot": "/optimization-co-pilot",

    "Feed Settings": "/feed-setting",
    "Catalogue List": "/catalogue-list",
    // "Product Statistics": "/feed-manager/product-statistics",
  };

  const findFirstAccessibleTab = (tabs) => {
    for (const tab of tabs) {
      if (tab.has_access) {
        if (tab.sub_tabs && tab.sub_tabs.length > 0) {
          const subTab = findFirstAccessibleTab(tab.sub_tabs);
          if (subTab) return subTab;
        }
        return tab;
      }
    }
    return null;
  };

  useEffect(() => {
    if (advertiserId && loginData?.role === "User") {
      const params = { advertiser_id: advertiserId };
      advertiserTabAccess(params, (response) => {
        if (response?.data?.result) {
          const tabData = response.data.result;
          setTabs(tabData);
          const firstAccessible = findFirstAccessibleTab(tabData);
          if (firstAccessible) {
            const route = tabRoutes[firstAccessible.name] || null;
            if (route) {
              if (
                location.pathname === "/audit-report" ||
                location.pathname === "/no-access"
              ) {
                navigate(route, { replace: true });
              }
            } else {
              if (location.pathname === "/audit-report") {
                navigate("/no-access", { replace: true });
              }
            }
          } else {
            if (location.pathname === "/audit-report") {
              navigate("/no-access", { replace: true });
            }
          }
        }
      });
    }
    // eslint-disable-next-line
  }, [advertiserId]);
  const refreshTabs = useCallback(() => {
    if (advertiserId && loginData?.role === "User") {
      advertiserTabAccess({ advertiser_id: advertiserId }, (response) => {
        if (response?.data?.result) {
          setTabs(response.data.result);
        }
      });
    }
  }, [advertiserId, advertiserTabAccess, loginData]);

  useEffect(() => {
    if (
      advertiserId ||
      dataIngestionStatus ||
      googleIngestionStatus ||
      optIngestionStatus ||
      // startDate ||
      // endDate ||
      selectedAdvertiser ||
      advLogo ||
      meta_followers ||
      insta_followers
    ) {
      const data = {
        advertiserId: advertiserId || "",
        dataIngestionStatus: dataIngestionStatus || "",
        googleIngestionStatus: googleIngestionStatus || "",
        optIngestionStatus: optIngestionStatus || "",
        // startDate: startDate || "",
        // endDate: endDate || "",
        selectedAdvertiser: selectedAdvertiser || "",
        advLogo: advLogo || "",
        meta_followers: meta_followers || "",
        insta_followers: insta_followers || "",
      };

      localStorage.setItem(advertiserId, JSON.stringify(data));
    }
  }, [
    advertiserId,
    dataIngestionStatus,
    googleIngestionStatus,
    optIngestionStatus,
    // startDate,
    // endDate,
    selectedAdvertiser,
    advLogo,
    meta_followers,
    insta_followers,
  ]);
  useEffect(() => {
    if (!isAdvLoading) {
      const activeAdvertisers = advertisersList.filter(
        (adv) => adv.is_active && !adv.is_archived,
      );
      if (isSearching) {
        setNoAdvertiser(false);
        return;
      }
      if (activeAdvertisers.length === 0) {
        if (
          location.pathname.startsWith("/configure") ||
          location.pathname === "/config-users" ||
          location.pathname === "/activity-log"
        ) {
          setNoAdvertiser(false);
        } else {
          setNoAdvertiser(true);
        }
      } else {
        setNoAdvertiser(false);
      }
    }
  }, [advertisersList, location.pathname, isAdvLoading, isSearching]);

  const memoizedGetAdvertisers = useCallback(getAdvertisers, [getAdvertisers]);
  useEffect(() => {
    // if (location.pathname !== "/export-pdf") {
    // console.log("========Effect ran");
    if (!loginData?.id) return;

    let isMounted = true;
    setIsAdvLoading(true);
    memoizedGetAdvertisers(
      {
        user_id: loginData?.id,
        // search: search
      },
      (response) => {
        try {
          if (!isMounted) return;
          if (response?.data?.result) {
            let advertisers = response?.data?.result?.rows || [];
            advertisers = advertisers.filter((adv) => !adv.is_archived);
            if (loginData?.email === "demo@prism.com") {
              advertisers = advertisers.filter(
                (adv) =>
                  adv.advertiser_id === "1677b400-8037-4b67-87f2-fc359750386b",
              );
            }
            const firstActiveAdvertiser = advertisers?.find(
              (advertiser) => advertiser?.is_active,
            );

            const storedAdvertiser = localStorage.getItem("currentAdv");
            if (storedAdvertiser) {
              // to set previously selected advertiser even on page refresh
              const parsedData = JSON.parse(storedAdvertiser);
              if (!parsedData.is_archived) {
                setAdvertisersList(advertisers);
                setAdvertiserId(parsedData?.advertiser_id || "");
                setSelectedAdvertiser(
                  parsedData?.advertiser_name || "Advertiser",
                );
                setMetaFollowers(
                  parsedData?.advertiser_data_config_details?.[0]
                    ?.meta_followers || "",
                );
                setInsFollowers(
                  parsedData?.advertiser_data_config_details?.[0]
                    ?.insta_followers || "",
                );
                setAdvLogo(parsedData?.logo || "");
                setDataIngestionStatus(
                  parsedData?.data_ingestion_statuses?.[0]?.status ?? null,
                );
                setGoogleIngestionStatus(
                  parsedData?.google_data_ingestion_statuses?.[0]?.status ??
                    null,
                );
                setOptIngestionStatus(
                  parsedData?.opti_data_ingestion_statuses?.[0]?.status || null,
                );
              } else {
                // fallback to firstActiveAdvertiser if archived
                const firstActiveAdvertiser = advertisers?.find(
                  (advertiser) =>
                    advertiser?.is_active && !advertiser?.is_archived,
                );
                if (firstActiveAdvertiser) {
                  setAdvertisersList(advertisers);
                  setAdvertiserId(firstActiveAdvertiser?.advertiser_id);
                  setSelectedAdvertiser(firstActiveAdvertiser?.advertiser_name);
                  setMetaFollowers(
                    firstActiveAdvertiser?.advertiser_data_config_details?.[0]
                      ?.meta_followers || "",
                  );
                  setInsFollowers(
                    firstActiveAdvertiser?.advertiser_data_config_details?.[0]
                      ?.insta_followers || "",
                  );
                  setAdvLogo(firstActiveAdvertiser?.logo);
                  setDataIngestionStatus(
                    firstActiveAdvertiser?.data_ingestion_statuses?.[0]
                      ?.status ?? null,
                  );
                  setGoogleIngestionStatus(
                    firstActiveAdvertiser?.google_data_ingestion_statuses?.[0]
                      ?.status ?? null,
                  );
                  setOptIngestionStatus(
                    firstActiveAdvertiser?.opti_data_ingestion_statuses?.[0]
                      ?.status || null,
                  );
                }
              }
            } else if (firstActiveAdvertiser) {
              setAdvertisersList(advertisers);
              setAdvertiserId(firstActiveAdvertiser?.advertiser_id);
              setSelectedAdvertiser(firstActiveAdvertiser?.advertiser_name);
              setMetaFollowers(
                firstActiveAdvertiser?.advertiser_data_config_details?.length
                  ? firstActiveAdvertiser?.advertiser_data_config_details[0]
                      ?.meta_followers
                  : null,
              );
              setInsFollowers(
                firstActiveAdvertiser?.advertiser_data_config_details[0]
                  ?.insta_followers,
              );
              setAdvLogo(firstActiveAdvertiser?.logo);
              setDataIngestionStatus(
                firstActiveAdvertiser.data_ingestion_statuses &&
                  firstActiveAdvertiser.data_ingestion_statuses.length > 0
                  ? firstActiveAdvertiser.data_ingestion_statuses[0].status
                  : null,
              );
              setGoogleIngestionStatus(
                firstActiveAdvertiser?.google_data_ingestion_statuses?.length
                  ? firstActiveAdvertiser.google_data_ingestion_statuses[0]
                      .status
                  : null,
              );
              setOptIngestionStatus(
                firstActiveAdvertiser?.opti_data_ingestion_statuses?.length
                  ? firstActiveAdvertiser.opti_data_ingestion_statuses[0].status
                  : null,
              );
              // setStartDate(
              //   firstActiveAdvertiser?.advertiser_data_config_details[0]
              //     ?.start_date
              // );
              // setEndDate(
              //   firstActiveAdvertiser?.advertiser_data_config_details[0]?.end_date
              // );
            } else {
              console.warn("No active advertiser found.");
            }
          }
        } catch (err) {
          console.error("Advertiser processing error:", err);
        } finally {
          if (isMounted) setIsAdvLoading(false); // ✅ ALWAYS
        }
      },
    );
    return () => {
      isMounted = false;
    };
    // }
    // eslint-disable-next-line
  }, [memoizedGetAdvertisers, loginData?.id]);
  const handleApiSuccess = useCallback(() => {
    memoizedGetAdvertisers({ user_id: loginData?.id }, (response) => {
      if (response?.data?.result) {
        let advertisers = response?.data?.result?.rows || [];
        advertisers = advertisers.filter(
          (adv) => adv.is_active && !adv.is_archived,
        );
        setAdvertisersList(advertisers);
        if (advertisers.length === 0) {
          console.warn("No valid advertiser found.");
          localStorage.removeItem("currentAdv");
          return;
        }
        let updatedAdvertiser = advertisers.find(
          (adv) => adv.advertiser_id === advertiserId,
        );
        if (!updatedAdvertiser) {
          updatedAdvertiser = advertisers[0];
        }
        setAdvertiserId(updatedAdvertiser.advertiser_id);
        setSelectedAdvertiser(updatedAdvertiser.advertiser_name);
        setMetaFollowers(
          updatedAdvertiser?.advertiser_data_config_details?.[0]
            ?.meta_followers || "",
        );
        setInsFollowers(
          updatedAdvertiser?.advertiser_data_config_details?.[0]
            ?.insta_followers || "",
        );
        setAdvLogo(updatedAdvertiser?.logo);
        setDataIngestionStatus(
          updatedAdvertiser?.data_ingestion_statuses?.[0]?.status ?? null,
        );
        setGoogleIngestionStatus(
          updatedAdvertiser?.google_data_ingestion_statuses?.[0]?.status ??
            null,
        );
        setOptIngestionStatus(
          updatedAdvertiser?.opti_data_ingestion_statuses?.[0]?.status || null,
        );
        localStorage.setItem("currentAdv", JSON.stringify(updatedAdvertiser));
      }
    });
  }, [advertiserId, memoizedGetAdvertisers, loginData?.id]);

  const toggleSidebar = () => {
    setSidebarOpen((prevState) => !prevState);
  };

  // useEffect(() => {
  //   const handleMouseMove = (e) => {
  //     const threshold = window.innerWidth * 0.05;
  //     if (e.clientX > threshold) {
  //       setSidebarOpen(true);
  //     } else {
  //       setSidebarOpen(false);
  //     }
  //   };

  //   window.addEventListener("mousemove", handleMouseMove);
  //   return () => window.removeEventListener("mousemove", handleMouseMove);
  // }, []);

  const handleLogout = async () => {
    const params = {
      module: 1,
      activity: 1,
      description: 1,
    };

    try {
      await addActivityLog(params); // Wait until activity is logged
    } catch (error) {
      console.error("Activity log failed", error); // Optional: log the error
    }
    userLogout();
    localStorage.clear();
    dispatch(clearUser());
    setOpenModal(false);
    navigate("/login");
  };
  const memoizedChildren = React.useMemo(
    () =>
      React.Children.map(children, (child) =>
        React.cloneElement(child, {
          advertiserId,
          dataIngestionStatus,
          googleIngestionStatus,
          optIngestionStatus,
          // startDate,
          // endDate,
          handleApiSuccess,
          advLogo,
          selectedAdvertiser,
          loginData,
          tabs,
          refreshTabs,
        }),
      ),
    [
      children,
      advertiserId,
      dataIngestionStatus,
      googleIngestionStatus,
      optIngestionStatus,
      // startDate,
      // endDate,
      handleApiSuccess,
      advLogo,
      selectedAdvertiser,
      loginData,
      tabs,
      refreshTabs,
    ],
  );

  const excludeHeader =
    location.pathname === "/configure-form" ||
    location.pathname === "/configure-optimization" ||
    location.pathname === "/configure-benchmark" ||
    location.pathname === "/configure" ||
    location.pathname === "/config-users" ||
    location.pathname === "/export-pdf" ||
    location.pathname === "/google-export-pdf" ||
    location.pathname === "/media-export-pdf" ||
    location.pathname === "/activity-log" ||
    location.pathname === "/advertiser-listing" ||
    noAdvertiser;
  const excludeSidebar =
    location.pathname === "/export-pdf" ||
    location.pathname === "/google-export-pdf" ||
    location.pathname === "/media-export-pdf";
  const excludeConfigHeader =
    location.pathname === "/configure-form" ||
    location.pathname === "/export-pdf" ||
    location.pathname === "/google-export-pdf" ||
    location.pathname === "/media-export-pdf" ||
    location.pathname === "/audit-report" ||
    location.pathname === "google-audit" ||
    location.pathname === "/media-planning" ||
    location.pathname === "/optimization-status" ||
    location.pathname === "/optimization-take-off" ||
    location.pathname === "/optimization-co-pilot" ||
    // location.pathname === "/campaign-analytics" ||
    // location.pathname === "/creative-ai" ||
    // location.pathname === "/audience" ||
    location.pathname === "/feed-setting" ||
    location.pathname === "/catalogue-list" ||
    location.pathname === "/no-access";

  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(true);
    }
  }, [location.pathname]);

  return (
    <div className={`wrapper ${isSidebarOpen ? "closed" : "expanded"}`}>
      <div onClick={toggleSidebar} className="sidebar-overlay lg:hidden"></div>
      {!excludeSidebar && (
        <SidebarComponent
          isOpen={isSidebarOpen}
          onToggleSidebar={toggleSidebar}
          setOpenModal={setOpenModal}
          loginData={loginData}
          tabs={tabs}
        />
      )}

      {/* <div
        className={`rightContainer duration-300 flex-1 ${
          isSidebarOpen ? "expanded" : "rightColl"
        }`}
      > */}
      <div className="rightContainer duration-300">
        {!excludeHeader && (
          <NavbarComponent
            isOpen={isSidebarOpen}
            onToggleSidebar={toggleSidebar}
            advertisersList={advertisersList}
            setAdvertiserId={setAdvertiserId}
            advertiserId={advertiserId}
            setSelectedAdvertiser={setSelectedAdvertiser}
            setMetaFollowers={setMetaFollowers}
            setInsFollowers={setInsFollowers}
            setAdvLogo={setAdvLogo}
            selectedAdvertiser={selectedAdvertiser}
            meta_followers={meta_followers}
            insta_followers={insta_followers}
            advLogo={advLogo}
            // setStartDate={setStartDate}
            // setEndDate={setEndDate}
            setDataIngestionStatus={setDataIngestionStatus}
            setGoogleIngestionStatus={setGoogleIngestionStatus}
            setOptIngestionStatus={setOptIngestionStatus}
            // search={search}
            // setSearch={setSearch}
            onSearchChange={(val) => {
              setIsSearching(val.trim() !== "");
              // only trigger advertiser API when typing
              memoizedGetAdvertisers(
                { user_id: loginData?.id, search: val },
                (response) => {
                  if (response?.data?.result) {
                    let advertisers = response.data.result.rows || [];
                    advertisers = advertisers.filter((adv) => !adv.is_archived);
                    setAdvertisersList(advertisers);
                  }
                },
              );
            }}
          />
        )}

        {excludeHeader && !excludeConfigHeader && <ConfigHeader />}

        {/* <div className="p-4 sectionWrap">{children}</div> */}
        {isAdvLoading ? (
          <Loader />
        ) : noAdvertiser ? (
          <div className="flex flex-col items-center justify-center min-h-screen text-gray-500">
            <h2 className="text-xl font-semibold">No Active Advertiser</h2>
          </div>
        ) : (
          <div className="p-4 sectionWrap">{memoizedChildren}</div>
        )}
      </div>
      <Modal
        show={openModal}
        size="md"
        onClose={() => setOpenModal(false)}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              Are you sure you want to Logout?
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={handleLogout}>
                {"Yes, I'm sure"}
              </Button>
              <Button color="gray" onClick={() => setOpenModal(false)}>
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Dashboard;
